/**
 * 「当前页面对应的 Nova One app 深链目标」——类型定义 + 全局登记处。
 *
 * ## 类型
 * `AppDeepLinkTarget` 是 registry 与 OpenAppBanner 的 URL 匹配**共用**的结构，
 * 由 `launchApp` 翻译成 app 侧的 scheme path。每个 kind 都对照 main-mobile 的
 * expo-router 路由文件核实过（2026-08-31）：
 *
 * | kind     | app 路由文件                              | scheme path            |
 * |----------|-------------------------------------------|------------------------|
 * | task     | `app/(main)/tasks/[id]/index.tsx`          | `tasks/{id}`           |
 * | issue    | `app/(main)/issues/[id]/index.tsx`         | `issues/{id}`          |
 * | asset    | `app/(main)/assets/[id]/page.tsx`          | `assets/{id}/page`     |
 * | purchase | `app/(main)/po-receiving/[id]/index.tsx`   | `po-receiving/{id}`    |
 *
 * intent 在 UI / app 上都叫 **issue**（见 entity-links.ts 的同款映射）。
 *
 * ## 登记处
 * 背景：Open-App banner（`src/components/share/OpenAppBanner.tsx`）挂在
 * RouterProvider **外层**，点击时只能读 `window.location` 来猜当前实体。
 * 对 `/tasks/:id`、`/intents?intentId=` 这类"URL 里带实体 id"的路由够用；
 * 但有些页面的实体 id **不在 URL 里**：
 *   - MCP 上传落地页 `/upload/:token`——token 是一次性凭证，实体要等 session 回来；
 *   - Material Request flyout `/material-requests?id={expenseId}`——URL 只有
 *     expense id，而 app 侧要跳的是**所属 task 的 supplies tab**。
 * 这类页面拿到实体后主动登记，banner 就能直达 app 内详情。
 *
 * 刻意做成模块级单例而不是 React context：banner 在 router 外、登记方在 router
 * 内，两边没有共同的 Provider 祖先；而且 banner 是**点击时**才读取（不订阅变化），
 * 一个可变的模块变量就足够，还省掉一次全局重渲染。
 *
 * 约定：登记方在实体到手时 `setAppDeepLinkTarget({...})`，**卸载 / 实体失效 /
 * flyout 关闭时必须置 null**——否则用户 SPA 导航到别的页面后，banner 还会把他
 * 带去上一个实体。
 */

export type AppDeepLinkTarget =
    | {
          kind: 'task';
          id: string;
          /**
           * app 侧 TaskDetailScreen 的 `?tab=`（如 `supplies`）——
           * 见 main-mobile `src/features/tasks/screens/TaskDetailScreen.tsx`。
           */
          tab?: string;
          /** 配合 `tab=supplies`：展开的那条 task_expense（`?focusSupply=`）。 */
          focusSupply?: string;
      }
    | { kind: 'issue'; id: string }
    | { kind: 'asset'; id: string }
    | { kind: 'purchase'; id: string };

let currentTarget: AppDeepLinkTarget | null = null;

/**
 * 登记（或用 null 清除）当前页面的 app 深链目标。
 * `id` 为空等价于清除——避免登记方传了个 `{ kind: 'task', id: '' }`
 * 就把 banner 的 URL 兜底逻辑给关掉，反而拼出一条打不开的深链。
 */
export function setAppDeepLinkTarget(target: AppDeepLinkTarget | null): void {
    currentTarget = target && target.id ? target : null;
}

/** 读取当前登记的深链目标；没有登记（或已清除）返回 null。 */
export function getAppDeepLinkTarget(): AppDeepLinkTarget | null {
    return currentTarget;
}
